import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CalendarDays, MapPin, Loader2, Users, Clock, Trash2, Pencil, CalendarPlus, Share2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { googleCalendarUrl, downloadIcs, shareEvent } from "@/lib/calendar";
import EventEditModal from "@/components/EventEditModal";
import { fmtDateTime } from "@/lib/date";

function fmtDate(iso) {
  return fmtDateTime(iso);
}

const STATUS = {
  approved: { label: "Published", cls: "bg-emerald-500/15 text-emerald-300" },
  pending: { label: "Pending review", cls: "bg-[#E6B34A]/15 text-[#E6B34A]" },
  rejected: { label: "Not approved", cls: "bg-[#FF3B30]/15 text-[#FF7A70]" },
};

export default function MyEvents() {
  const [data, setData] = useState({ created: [], attending: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/events/mine").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (ev) => {
    try { await api.delete(`/events/${ev.id}`); load(); toast.success("Event removed"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const cancelRsvp = async (ev) => {
    try { await api.post(`/events/${ev.id}/rsvp`); load(); toast.success("RSVP removed"); }
    catch (e) { toast.error("Could not update"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10" data-testid="my-events-page">
      <div className="flex items-center gap-3 mb-1">
        <CalendarCheck className="w-6 h-6 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">My Events</h1>
      </div>
      <p className="text-zinc-400 mb-8">Events you're attending and the ones you've shared with the community.</p>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div> : (
        <div className="space-y-10">
          <Section title="Attending" icon={Users} testid="my-events-attending" empty="You haven't RSVP'd to any events yet." link>
            {data.attending.map((ev) => (
              <EventRow key={ev.id} ev={ev}>
                <CalendarButtons ev={ev} />
                <button onClick={() => cancelRsvp(ev)} data-testid={`myev-cancel-${ev.id}`} className="text-xs text-zinc-500 hover:text-[#FF3B30] transition-colors">Cancel RSVP</button>
              </EventRow>
            ))}
          </Section>

          <Section title="Created by me" icon={CalendarDays} testid="my-events-created" empty="You haven't created any events yet." link>
            {data.created.map((ev) => (
              <EventRow key={ev.id} ev={ev} showStatus>
                {ev.status === "approved" && <CalendarButtons ev={ev} />}
                <button onClick={() => setEditing(ev)} data-testid={`myev-edit-${ev.id}`} className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-[#E6B34A] transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => del(ev)} data-testid={`myev-del-${ev.id}`} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#FF3B30] transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </EventRow>
            ))}
          </Section>
        </div>
      )}
      {editing && <EventEditModal event={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function Section({ title, icon: Icon, children, empty, testid, link }) {
  const hasChildren = React.Children.count(children) > 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-[#E6B34A]" />
        <h2 className="font-display font-bold text-xl tracking-tight">{title}</h2>
      </div>
      {hasChildren ? <div className="space-y-3">{children}</div> : (
        <p className="text-zinc-500 text-sm py-8 text-center border border-dashed border-white/10 rounded-2xl">
          {empty} {link && <Link to="/events" className="text-[#E6B34A] font-semibold">Browse events →</Link>}
        </p>
      )}
    </div>
  );
}

function EventRow({ ev, children, showStatus }) {
  const st = STATUS[ev.status] || STATUS.pending;
  const onShare = async () => {
    const r = await shareEvent(ev);
    if (r === "copied") toast.success("Invite copied!");
    else if (r === "error") toast.error("Could not share");
  };
  return (
    <div className="rounded-2xl border border-white/5 bg-[#141C42] overflow-hidden" data-testid={`myev-${ev.id}`}>
      {ev.has_cover && <img src={`${api.defaults.baseURL}/events/${ev.id}/cover`} alt={ev.title} className="w-full h-32 object-cover" />}
      <div className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold truncate">{ev.title}</h3>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-zinc-300">{ev.event_type}</span>
            {ev.featured && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#E6B34A]/15 text-[#E6B34A]">★ Featured</span>}
            {showStatus && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>}
          </div>
          <p className="text-sm text-[#E6B34A] mt-1.5 flex items-center gap-1.5"><Clock className="w-4 h-4" /> {fmtDate(ev.starts_at)}</p>
          <p className="text-sm text-zinc-400 mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4 shrink-0" /> {[ev.venue, ev.city, ev.country].filter(Boolean).join(", ")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={onShare} data-testid={`myev-share-${ev.id}`} title="Share" className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors"><Share2 className="w-4 h-4" /></button>
          <span className="text-xs text-zinc-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {ev.attendee_count}</span>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-4 flex-wrap">{children}</div>
      </div>
    </div>
  );
}

export function CalendarButtons({ ev }) {
  return (
    <div className="flex items-center gap-2">
      <a href={googleCalendarUrl(ev)} target="_blank" rel="noreferrer" data-testid={`gcal-${ev.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold transition-colors">
        <CalendarPlus className="w-3.5 h-3.5" /> Google
      </a>
      <button onClick={() => downloadIcs(ev)} data-testid={`ics-${ev.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold transition-colors">
        <CalendarPlus className="w-3.5 h-3.5" /> Apple / .ics
      </button>
    </div>
  );
}
